import { Box, CircularProgress, Typography } from '@mui/material';
import type { SetStateAction } from 'react';
import { useCallback, useMemo, useState } from 'react';

import { useTickets } from 'contexts/TicketsContext';
import { TicketsRefinementKeysE } from 'declarations/enums/tickets';
import type { SubFilterI } from 'declarations/interfaces/common';
import type { ApiErrorI } from 'declarations/interfaces/errors';

import { InsightsDealerGroupSelect } from './components/InsightsDealerGroupSelect/InsightsDealerGroupSelect';
import { InsightsDealerSelect } from './components/InsightsDealerSelect/InsightsDealerSelect';
import { InsightsOemSelect } from './components/InsightsOemSelect/InsightsOemSelect';
import { ReportItem } from './components/ReportItem';
import SelectCategoryReport from './components/SelectCategories/SelectCategoryReport';
import type {
  DashboardI,
  GetDashboardsResponseDTO,
  GetEmbedUrlResponseDTO,
} from './declarations/reporting';
import { useFetchDashboardIds, useFetchEmbedUrl } from './hooks/useReporting';

const InsightsContent = (): JSX.Element => {
  const [dashboards, setDashboards] = useState<DashboardI[]>();
  const [selectedDashboard, setSelectedDashboard] = useState<DashboardI | undefined>(undefined);
  const [embedUrl, setEmbedUrl] = useState<GetEmbedUrlResponseDTO | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const {
    roofTopsList,
    oemFilter,
    setOemFilter,
    dealerGroupFilter,
    setDealerGroupFilter,
    getSearchParam,
    updateSearchParamsArray,
  } = useTickets();

  const selectedDealerId = getSearchParam(TicketsRefinementKeysE.RoofTopId) ?? null;

  /** Dealer groups displayed in dropdown: when OEM filter is active, only show groups that have rooftops for those OEMs. */
  const insightsDealerGroupFilterDisplayed = useMemo((): SubFilterI[] => {
    const activeOemIds = oemFilter.filter((o): boolean => o.checked).map((o): string => o.value);
    if (activeOemIds.length === 0) return dealerGroupFilter;
    const accountIdsWithSelectedOems = new Set(
      (roofTopsList ?? [])
        .filter((r): boolean => activeOemIds.includes(r.oemFamilyId))
        .map((r): string => r.accountId)
    );
    return dealerGroupFilter.filter((g): boolean => accountIdsWithSelectedOems.has(g.value));
  }, [dealerGroupFilter, oemFilter, roofTopsList]);

  /** Dealers filtered by OEM and Dealer Group (for dropdown list). */
  const insightsFilteredRoofTops = useMemo((): typeof roofTopsList => {
    let list = roofTopsList ?? [];
    const activeOemIds = oemFilter.filter((o): boolean => o.checked).map((o): string => o.value);
    const activeDealerGroupIds = dealerGroupFilter
      .filter((g): boolean => g.checked)
      .map((g): string => g.value);
    if (activeDealerGroupIds.length > 0) {
      list = list.filter((r): boolean => activeDealerGroupIds.includes(r.accountId));
    }
    if (activeOemIds.length > 0) {
      list = list.filter((r): boolean => activeOemIds.includes(r.oemFamilyId));
    }
    return list;
  }, [roofTopsList, oemFilter, dealerGroupFilter]);

  const roofTopIds = useMemo((): string[] => {
    const list = insightsFilteredRoofTops ?? [];
    if (selectedDealerId && selectedDealerId.trim()) {
      return [selectedDealerId.trim().toUpperCase()];
    }
    return list.map((r): string => r.id.toUpperCase());
  }, [insightsFilteredRoofTops, selectedDealerId]);

  const handleDealerChange = useCallback(
    (id: string | null): void => {
      updateSearchParamsArray(TicketsRefinementKeysE.RoofTopId, id ? [id] : null);
    },
    [updateSearchParamsArray]
  );

  const handleOemChange = useCallback((): void => {
    setDealerGroupFilter((prev): SubFilterI[] =>
      prev.map((g): SubFilterI => ({ ...g, checked: false }))
    );
    updateSearchParamsArray(TicketsRefinementKeysE.RoofTopId, null);
  }, [setDealerGroupFilter, updateSearchParamsArray]);

  const handleDealerGroupChange = useCallback((): void => {
    updateSearchParamsArray(TicketsRefinementKeysE.RoofTopId, null);
  }, [updateSearchParamsArray]);

  const handleDealerGroupSelectChange = useCallback(
    (updatedFilters: SubFilterI[]): void => {
      setDealerGroupFilter((prev): SubFilterI[] =>
        prev.map((g): SubFilterI => {
          const updated = updatedFilters.find((f): boolean => f.value === g.value);
          return updated ?? g;
        })
      );
      updateSearchParamsArray(TicketsRefinementKeysE.RoofTopId, null);
    },
    [setDealerGroupFilter, updateSearchParamsArray]
  );

  useFetchDashboardIds({
    onSuccess: (data: GetDashboardsResponseDTO): void => {
      const newDashboards = (data as DashboardI[]).map(
        (dashboard): DashboardI => ({
          ...dashboard,
        })
      );
      const defaultDashboard = newDashboards[0];
      setDashboards(newDashboards);
      setSelectedDashboard(defaultDashboard);
      setLoading(false);
    },
    onError: (error: { code: SetStateAction<string | null> }): void => {
      setLoading(false);
      setError(error.code);
    },
  });

  useFetchEmbedUrl({
    dashboardId: selectedDashboard?.dashboardId ?? '',
    roofTopIds: roofTopIds,
    onSuccess: (data: GetEmbedUrlResponseDTO): void => {
      setEmbedUrl(data);
      setLoading(false);
    },
    onError: (error: ApiErrorI): void => {
      setLoading(false);
      if (error.data?.status === 403) {
        setError('You do not have access to this dashboard.');
      } else {
        setError(error.code);
      }
    },
  });

  const handleDashboardClick = (dashboard: DashboardI): void => {
    setError(null);
    setSelectedDashboard(dashboard);
  };

  return (
    <Box
      sx={{
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflowY: 'inherit',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '0.75rem',
          justifyContent: 'flex-start',
          alignItems: 'center',
          margin: '8px 0',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <InsightsOemSelect
            value={oemFilter}
            onChange={setOemFilter}
            onOemChange={handleOemChange}
          />
          <InsightsDealerGroupSelect
            value={insightsDealerGroupFilterDisplayed}
            onChange={handleDealerGroupSelectChange}
            onDealerGroupChange={handleDealerGroupChange}
          />
          <InsightsDealerSelect
            roofTops={insightsFilteredRoofTops ?? []}
            roofTopsList={roofTopsList}
            value={selectedDealerId}
            onChange={handleDealerChange}
          />
          {selectedDashboard && (
            <SelectCategoryReport
              dashboards={dashboards}
              value={selectedDashboard}
              onChange={handleDashboardClick}
              isLoading={loading}
            />
          )}
        </Box>
      </Box>
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" flexGrow={1}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Box display="flex" justifyContent="center" alignItems="center" flexGrow={1}>
            <Typography variant="h5" color="error">
              {error}
            </Typography>
          </Box>
        )}

        {!loading && embedUrl && (
          <Box sx={{ flexGrow: 1, height: '100%', display: 'flex' }}>
            <ReportItem
              id={embedUrl.dashboardId}
              route={embedUrl.embedURL ?? ''}
              roofTopIds={roofTopIds}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default InsightsContent;
